import React, { useEffect, useState } from "react";
import { DateRangePicker, Calendar } from "react-date-range";
import { addMonths, addYears, getHours, isSameDay } from "date-fns";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  Box,
  Paper,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Popper,
  Fade,
  ClickAwayListener,
  Tooltip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file
import PrimaryButton from "./PrimaryButton";
import SecondaryButton from "./SecondaryButton";
import {
  parseDateTimeDay,
  parseDateTimeFromTo,
  parseDateTimeRange,
} from "../../utils/parseDateTimeRange";

const createStaticRange = (ranges, staticRangeHandler) => {
  return ranges.map((range) => ({ ...staticRangeHandler, ...range }));
};

const staticRanges = [
  {
    label: "6 Months",
    // hasCustomRendering: true,
    range() {
      return {
        startDate: addMonths(new Date(), -6),
        endDate: new Date(),
      };
    },
  },
  {
    label: "1 Year",
    // hasCustomRendering: true,
    range() {
      return {
        startDate: addYears(new Date(), -1),
        endDate: new Date(),
      };
    },
  },
  {
    label: "3 Years",
    // hasCustomRendering: true,
    range() {
      return {
        startDate: addYears(new Date(), -3),
        endDate: new Date(),
      };
    },
  },
  {
    label: "5 Years",
    // hasCustomRendering: true,
    range() {
      return {
        startDate: addYears(new Date(), -5),
        endDate: new Date(),
      };
    },
  },
];

const isCustomSelected = (range) => {
  // Check that ranges doesn't exist in staticRanges
  return (
    !staticRanges.find((staticRange) => {
      return (
        isSameDay(staticRange.range().startDate, range.startDate) &&
        isSameDay(staticRange.range().endDate, range.endDate)
      );
    }) && !isSameDay(range.startDate, range.endDate)
  );
};

const CustomTimePicker = ({ initialDate, onChange }) => {
  const [timePicker, setTimePicker] = useState({
    open: false,
    value: initialDate,
  });

  const openTimePicker = () => {
    setTimePicker({ ...timePicker, open: true });
  };
  const closeTimePicker = () => {
    setTimePicker({ ...timePicker, open: false });
  };
  const handleChange = (value) => {
    onChange(value);
  };

  return (
    <TimePicker
      value={initialDate}
      onChange={handleChange}
      open={timePicker.open}
      onOpen={openTimePicker}
      onClose={closeTimePicker}
      components={{
        OpenPickerIcon: KeyboardArrowDownIcon,
      }}
      renderInput={(params) => (
        <TextField {...params} onClick={openTimePicker} size="small" />
      )}
      PaperProps={{
        sx: {
          "& .MuiClock-root": {
            "& .MuiClock-amButton": {
              backgroundColor:
                getHours(initialDate) < 12 ? "var(--primary)" : "transparent",
            },
            "& .MuiClock-pmButton": {
              backgroundColor:
                getHours(initialDate) >= 12 ? "var(--primary)" : "transparent",
            },
          },
        },
      }}
      PopperProps={{
        sx: {
          zIndex: 5000,
        },
      }}
    />
  );
};

const DayPicker = ({ selectedDay, setSelectedDay }) => {
  const handleChange = (date) => {
    setSelectedDay(date);
  };

  return (
    <Calendar
      date={selectedDay}
      onChange={handleChange}
      className="dayPicker"
    />
  );
};

const DateTimePicker = ({
  initialTimePicker,
  initialDatePicker,
  onApply,
  onCancel,
  type = "ranges",
}) => {
  const [datePicker, setDatePicker] = useState({
    ...initialDatePicker,
    key: "selection",
  });

  const [timePicker, setTimePicker] = useState({
    ...initialTimePicker,
  });

  const [selectedDay, setSelectedDay] = useState(datePicker.startDate);

  const isRanges = type === "ranges";

  const staticRangeHandler = {
    range: {},
    isSelected(range) {
      const definedRange = this.range();
      const selected =
        isSameDay(range.startDate, definedRange.startDate) &&
        isSameDay(range.endDate, definedRange.endDate);
      return selected;
    },
  };

  const handleDateRangeChange = (item) => {
    setDatePicker(item.selection);
  };

  const handleTimePickerChange = (name, value) => {
    const updatedTimePicker = { ...timePicker, [name]: value };
    setTimePicker(updatedTimePicker);
  };

  const handleApply = () => {
    const dateValue = isRanges
      ? datePicker
      : { startDate: selectedDay, endDate: selectedDay };
    onApply({ datePicker: dateValue, timePicker });
  };
  const handleCancel = () => {
    onCancel();
  };

  return (
    <Stack component={Paper} elevation={4} width={"min-content"}>
      <Box
        sx={{
          borderBottom: "1px solid #E7E9EF",
          display: "flex",
          justifyContent: "center",
        }}
        position="relative"
        width="100%"
      >
        {isRanges ? (
          <DateRangePicker
            onChange={handleDateRangeChange}
            showSelectionPreview={true}
            moveRangeOnFirstSelection={false}
            ranges={[datePicker]}
            inputRanges={[]}
            staticRanges={createStaticRange(
              [
                ...staticRanges,
                {
                  label: "Custom",
                  range(args) {
                    return {
                      startDate: datePicker.startDate,
                      endDate: datePicker.endDate,
                    };
                  },
                  isSelected() {
                    const definedRange = this.range();
                    return isCustomSelected(definedRange);
                  },
                },
              ],
              staticRangeHandler
            )}
            className="centeredDateRangePicker"
          />
        ) : (
          <DayPicker {...{ selectedDay, setSelectedDay }} />
        )}
      </Box>
      <Stack
        direction={isRanges ? "row" : "column"}
        width="100%"
        alignItems="center"
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            width={isRanges ? "62%" : "90%"}
            padding={isRanges ? "1rem" : "0.5rem 0rem"}
          >
            <CustomTimePicker
              initialDate={timePicker.startDate}
              onChange={(value) => handleTimePickerChange("startDate", value)}
            />
            <CustomTimePicker
              initialDate={timePicker.endDate}
              onChange={(value) => handleTimePickerChange("endDate", value)}
            />
          </Stack>
        </LocalizationProvider>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent={isRanges ? "flex-end" : "center"}
          width={isRanges ? "40%" : "90%"}
          padding={isRanges ? "1rem" : "0.5rem 1rem"}
        >
          <PrimaryButton
            onClick={handleCancel}
            sx={{
              width: "50%",
            }}
          >
            Cancel
          </PrimaryButton>
          <SecondaryButton
            sx={{
              backgroundColor: "var(--primary)",
              "&:hover": { backgroundColor: "var(--primary)" },
              width: "50%",
            }}
            onClick={handleApply}
          >
            Apply
          </SecondaryButton>
        </Stack>
      </Stack>
    </Stack>
  );
};

const ArrowIcon = (open) =>
  open ? (
    <ExpandLessIcon fontSize="small" />
  ) : (
    <ExpandMoreIcon fontSize="small" />
  );

const getInitialState = (dateTimeFrom, dateTimeTo, type = "ranges") => {
  return {
    open: false,
    anchorEl: null,
    displayValue: "",
    datePicker: {
      startDate: new Date(dateTimeFrom),
      endDate: new Date(dateTimeTo),
    },
    timePicker: {
      startDate: new Date(dateTimeFrom),
      endDate: new Date(dateTimeTo),
    },
  };
};

const DateTimePickerWithTextField = ({
  dateTimeFrom,
  dateTimeTo,
  label = "",
  icon = "arrow",
  showTooltip = false,
  isInvalid = false,
  onChange = () => {},
  type = "ranges",
  position = "bottom-start",
}) => {
  const [dateTimePicker, setDateTimePicker] = useState({
    ...getInitialState(dateTimeFrom, dateTimeTo, type),
  });

  const handlePickerClick = (event) => {
    setDateTimePicker({
      ...dateTimePicker,
      open: !dateTimePicker.open,
      anchorEl: event.currentTarget,
    });
  };

  const hanldePickerClickAway = () => {
    setDateTimePicker({
      ...dateTimePicker,
      open: false,
      anchorEl: null,
    });
  };

  const handlePickerApply = ({ timePicker, datePicker }) => {
    setDateTimePicker({
      ...dateTimePicker,
      open: false,
      anchorEl: null,
      timePicker,
      datePicker,
    });
    onChange({
      dateTimeFrom: parseDateTimeFromTo(
        datePicker.startDate,
        timePicker.startDate
      ),
      dateTimeTo: parseDateTimeFromTo(datePicker.endDate, timePicker.endDate),
    });
  };
  const handlePickerCancel = () => {
    setDateTimePicker({
      ...dateTimePicker,
      open: false,
      anchorEl: null,
    });
  };

  const canPickerBeOpen =
    dateTimePicker.open && Boolean(dateTimePicker.anchorEl);
  const pickerId = canPickerBeOpen ? "date-time-picker" : undefined;
  const displayValue =
    type === "ranges"
      ? parseDateTimeRange(dateTimePicker.datePicker, dateTimePicker.timePicker)
      : parseDateTimeDay(
          dateTimePicker.datePicker.startDate,
          dateTimePicker.timePicker
        );

  useEffect(() => {
    setDateTimePicker({ ...getInitialState(dateTimeFrom, dateTimeTo, type) });
  }, [dateTimeFrom, dateTimeTo, type]);
  return (
    <>
      <Tooltip title={showTooltip ? displayValue : undefined}>
        <TextField
          size="small"
          label={label}
          value={displayValue}
          sx={{ background: "#fff" }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="open datetime picker"
                  edge="end"
                  size="small"
                >
                  {icon === "arrow" ? (
                    <ArrowIcon open={dateTimePicker.open} />
                  ) : (
                    <CalendarTodayIcon fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          onClick={handlePickerClick}
          aria-describedby={pickerId}
          readOnly
          fullWidth
          error={isInvalid}
        />
      </Tooltip>
      {dateTimePicker.open && (
        <ClickAwayListener onClickAway={hanldePickerClickAway}>
          <Popper
            id={pickerId}
            open={dateTimePicker.open}
            anchorEl={dateTimePicker.anchorEl}
            placement={position}
            transition
            sx={{ zIndex: 4000 }}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <Box>
                  <DateTimePicker
                    onApply={handlePickerApply}
                    onCancel={handlePickerCancel}
                    initialDatePicker={dateTimePicker.datePicker}
                    initialTimePicker={dateTimePicker.timePicker}
                    type={type}
                  />
                </Box>
              </Fade>
            )}
          </Popper>
        </ClickAwayListener>
      )}
    </>
  );
};

export default DateTimePickerWithTextField;
